import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { APIError, useAxios } from '../core/_axios';
import { requestCodeForEmailAddress, loginWithPassword } from '../core/_requests';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

// reCAPTCHA Enterprise site key (same as Register page)
const RECAPTCHA_SITE_KEY = '6LeKzhQsAAAAAGlinDX1qnJBCLzdmrw5b0lB1wyH';

// Declare grecaptcha type for Enterprise
declare global {
    interface Window {
        grecaptcha: {
            enterprise: {
                ready: (callback: () => void) => void;
                execute: (siteKey: string, options: { action: string }) => Promise<string>;
            };
        };
    }
}

/**
 * Get reCAPTCHA token - in production uses real reCAPTCHA, in development simulates
 * the async behavior to catch timing-related bugs locally.
 */
const getRecaptchaToken = async (action: string): Promise<string | null> => {
    if (process.env.NODE_ENV === 'production') {
        await new Promise<void>((resolve) => {
            window.grecaptcha.enterprise.ready(() => resolve());
        });

        return await window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, {
            action
        });
    } else {
        // Development: simulate async behavior to catch timing bugs
        await new Promise<void>((resolve) => setTimeout(resolve, 50));
        return null;
    }
};

const Login = () => {
    const { login } = useAuth();
    const { copy } = useLocalizedCopy();

    const axios = useAxios();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Check for password reset success message
    const passwordResetSuccess = searchParams.get('password_reset') === 'success';

    // Login mode: 'code' (default) or 'password'
    const [loginMode, setLoginMode] = useState<'code' | 'password'>('code');

    const [emailAddress, setEmailAddress] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load reCAPTCHA Enterprise script for password mode
    useEffect(() => {
        if (process.env.NODE_ENV === 'production' && loginMode === 'password') {
            const existingScript = document.querySelector(`script[src*="recaptcha/enterprise.js"]`);
            if (existingScript) return;

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
            script.async = true;
            document.head.appendChild(script);
        }
    }, [loginMode]);

    const handleCodeLogin = useCallback(async () => {
        if (submitting) return;

        setSubmitting(true);
        setError(null);
        try {
            await requestCodeForEmailAddress(axios, emailAddress);
            const cleanedAndDowncasedEmail = emailAddress.trim().toLowerCase();
            navigate(`/auth/login-confirm?e=${encodeURIComponent(cleanedAndDowncasedEmail)}`);
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : copy.Auth.login.errorMessage;
            setError(errorMessage);
            setSubmitting(false);
        }
    }, [axios, emailAddress, navigate, submitting, copy.Auth.login.errorMessage]);

    const handlePasswordLogin = useCallback(async () => {
        if (submitting) return;

        setSubmitting(true);
        setError(null);
        try {
            const recaptchaToken = await getRecaptchaToken('PASSWORD_LOGIN');
            const response = await loginWithPassword(axios, emailAddress, password, recaptchaToken);

            // Login and redirect
            login(response.api_token);

            // Check for marketing lead onboarding
            const isMarketingLead = response.user?.public_data?.is_marketing_lead;
            const needsOnboarding = response.user?.public_data?.needs_onboarding;

            if (isMarketingLead && needsOnboarding) {
                navigate('/welcome');
            } else {
                navigate('/');
            }
        } catch (exception) {
            console.error(exception);
            const errorMessage = exception instanceof APIError ? exception.message : copy.Auth.login.errorMessage;
            setError(errorMessage);
            setSubmitting(false);
        }
    }, [axios, emailAddress, password, submitting, login, navigate, copy.Auth.login.errorMessage]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (loginMode === 'code') {
            handleCodeLogin();
        } else {
            handlePasswordLogin();
        }
    }, [loginMode, handleCodeLogin, handlePasswordLogin]);

    return (
        <div className="flex items-center justify-center grow bg-center bg-no-repeat page-bg">
            <div className="card max-w-[420px] w-full">
                <form className="card-body flex flex-col gap-5 p-10" id="sign_in_form" onSubmit={handleSubmit}>
                    <div className="text-center mb-2.5">
                        <div className="flex items-center justify-center mb-5">
                            <img
                                className="small-logo min-h-[22px] max-h-[50px] max-w-100"
                                src="/design/web_logo_black_small.png"
                                alt="Small Logo"
                            />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 leading-none mb-2.5">
                            {copy.Auth.login.title}
                        </h3>
                        <div className="flex items-center justify-center font-medium">
                            <span className="text-2sm text-gray-600 me-1.5">
                                {copy.Auth.login.needAccount}
                            </span>
                            <Link className="text-2sm link" to="/auth/register">
                                {copy.Auth.login.registerLink}
                            </Link>
                        </div>
                    </div>

                    {/* Password reset success message */}
                    {passwordResetSuccess && (
                        <div className="text-center p-4 text-sm text-green-800 rounded-lg bg-green-50" role="alert">
                            {copy.Auth.resetPassword?.successMessage || 'Your password has been reset successfully. You can now sign in.'}
                        </div>
                    )}

                    {/* Email field - always shown */}
                    <div className="flex flex-col gap-1">
                        <label className="form-label text-gray-900">
                            {copy.Auth.login.emailLabel}
                        </label>
                        <input
                            id="emailAddress"
                            name="emailAddress"
                            className="input"
                            placeholder={copy.Auth.login.emailPlaceholder}
                            type="email"
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    {/* Password field - only shown in password mode */}
                    {loginMode === 'password' && (
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between gap-1">
                                <label className="form-label text-gray-900">
                                    {copy.Auth.login.passwordLabel}
                                </label>
                                <Link className="text-2sm link shrink-0" to="/auth/forgot-password">
                                    {copy.Auth.login.forgotPassword}
                                </Link>
                            </div>
                            <input
                                id="password"
                                name="password"
                                className="input"
                                placeholder={copy.Auth.login.passwordPlaceholder}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                    )}

                    {/* Info text - only for code mode */}
                    {loginMode === 'code' && (
                        <div className="flex items-center justify-center">
                            <div className="text-gray-600 text-2sm text-center">
                                {copy.Auth.login.codeEmailMessage}
                            </div>
                        </div>
                    )}

                    {/* Submit button */}
                    <button
                        className="btn btn-primary flex justify-center grow"
                        type="submit"
                        disabled={submitting}
                    >
                        {loginMode === 'code'
                            ? (!submitting ? copy.Auth.login.sendLoginCode : copy.Auth.login.sending)
                            : (!submitting ? (copy.Auth.login.signIn || 'Sign In') : (copy.Auth.login.signingIn || 'Signing in...'))
                        }
                    </button>

                    {/* Error message */}
                    {error && (
                        <div className="text-center p-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                            {error}
                        </div>
                    )}

                    {/* Toggle between login modes */}
                    <div className="text-center">
                        <button
                            type="button"
                            className="text-2sm link"
                            onClick={() => {
                                setLoginMode(loginMode === 'code' ? 'password' : 'code');
                                setError(null);
                            }}
                        >
                            {loginMode === 'code'
                                ? (copy.Auth.login.signInWithPassword || 'Sign in with password')
                                : (copy.Auth.login.signInWithCode || 'Sign in with email code')
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
